/**
 * Navigation History Service
 * Tracks user navigation history to enable true breadcrumb functionality
 */
class NavigationHistoryService {
  constructor() {
    this.history = []
    this.maxHistoryLength = 10
  }

  /**
   * Add a route to the navigation history
   * @param {Object} route - Vue router route object
   */
  addToHistory(route) {
    const breadcrumb = {
      name: route.name,
      params: { ...route.params },
      query: { ...route.query },
      path: route.path,
      title: this.getRouteTitle(route),
      timestamp: Date.now(),
    }

    // Avoid duplicates of the same route
    if (this.history.length === 0 || this.history[this.history.length - 1].path !== route.path) {
      this.history.push(breadcrumb)
    }

    // Keep history manageable
    if (this.history.length > this.maxHistoryLength) {
      this.history.shift()
    }
  }

  /**
   * Get the previous route in history
   * @returns {Object|null} Previous route or null if no history
   */
  getPreviousRoute() {
    return this.history.length >= 1 ? this.history[this.history.length - 1] : null
  }

  /**
   * Get the current route in history
   * @returns {Object|null} Previous route or null if no history
   */
  getCurrentRoute(route) {
    return {
      name: route.name,
      params: route.params,
      query: route.query,
      path: route.path,
      title: this.getRouteTitle(route),
    }
  }

  /**
   * Get the full navigation history
   * @returns {Array} Array of route objects
   */
  getHistory() {
    return [...this.history]
  }

  /**
   * Clear the navigation history
   */
  clear() {
    this.history = []
  }

  /**
   * Get a user-friendly title for a route
   * @param {Object} route - Vue router route object
   * @returns {string} User-friendly title
   */
  getRouteTitle(route) {
    // Map route names to user-friendly titles
    const titleMap = {
      'quote-overview-page': 'Dashboard',
      'configuration-page': `Order ${route.params.quoteId}`,
      'change-orders-page': 'Change Orders',
      'change-order-page': `Change Order ${route.params.changeOrderId}`,
      'new-change-order-page': 'New Change Order',
      'purchase-orders-page': 'Purchase Orders',
      'purchase-order-page': `Purchase Order ${route.params.purchaseOrderId}`,
      'new-purchase-order-page': 'New Purchase Order',
      'bulk-change-order-page': `Bulk Change Order ${route.params.bulkChangeOrderId}`,
      'establishment-selection': 'Vestiging Selectie',
    }

    return titleMap[route.name] || route.name || 'Unknown Page'
  }
}

// Export singleton instance
export default new NavigationHistoryService()
